import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { SapEtaItemModel } from './models/sap-eta.model';
import { Error, CustomHttpErrorResponse } from '../../../../common/constants/error';
import { NotificationService } from '../../../../common/services/notification/notification.service';
import { Observable, of } from 'rxjs';
import { SimpleSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { catchError, map } from 'rxjs/operators';
import {
  SapEtaConfigurationListLoad,
  SapEtaConfigurationListInitialState,
  SapEtaConfigurationDelete,
  SapEtaConfigurationUpdate,
  SapEtaConfigurationCreate,
  SapEtaManualPush,
} from './sap-eta-configuration.actions';
import { SapEtaConfigurationService } from './sap-eta-configuration.service';

export type SapEtaConfigurationStateModel = {
  loaded: boolean;
  items: SapEtaItemModel[];
};

const initialState: SapEtaConfigurationStateModel = {
  loaded: false,
  items: [],
};

@State({
  name: 'sapEtaConfigurationList',
  defaults: initialState,
})
@Injectable()
export class SapEtaConfigurationState {
  constructor(
    private sapEtaConfigurationListService: SapEtaConfigurationService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: SapEtaConfigurationStateModel): SapEtaConfigurationStateModel {
    return state;
  }

  @Selector()
  static getItems(state: SapEtaConfigurationStateModel): SapEtaItemModel[] {
    return state.items;
  }

  @Selector()
  static getLoaded(state: SapEtaConfigurationStateModel): boolean {
    return state.loaded;
  }

  @Action(SapEtaConfigurationListLoad)
  getSapEtaConfigurationList(
    ctx: StateContext<SapEtaConfigurationStateModel>,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    ctx.patchState({ loaded: false });

    return this.sapEtaConfigurationListService.getList().pipe(
      map((items: SapEtaItemModel[]) => {
        ctx.patchState({ items, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(SapEtaConfigurationCreate)
  sapEtaConfigurationCreate(
    ctx: StateContext<SapEtaConfigurationStateModel>,
    { payload }: SapEtaConfigurationCreate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    ctx.patchState({ loaded: false });

    return this.sapEtaConfigurationListService.create(payload).pipe(
      map(() => {
        ctx.patchState({ loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(SapEtaConfigurationUpdate)
  sapEtaConfigurationUpdate(
    ctx: StateContext<SapEtaConfigurationStateModel>,
    { payload }: SapEtaConfigurationUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    ctx.patchState({ loaded: false });

    return this.sapEtaConfigurationListService.update(payload).pipe(
      map(() => {
        ctx.patchState({ loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(SapEtaConfigurationDelete)
  sapEtaConfigurationDelete(
    ctx: StateContext<SapEtaConfigurationStateModel>,
    { payload }: SapEtaConfigurationDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    ctx.patchState({ loaded: false });

    return this.sapEtaConfigurationListService.delete(payload).pipe(
      map(() => {
        ctx.patchState({ loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(SapEtaManualPush)
  sapEtaManualPush(
    _ctx: StateContext<SapEtaConfigurationStateModel>,
    { payload }: SapEtaManualPush,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.sapEtaConfigurationListService.pushShipments(payload).pipe(
      map(() => this.notificationService.success('api.sapEtaConfiguration.success.manualPush')),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(SapEtaConfigurationListInitialState)
  setInitialState(ctx: StateContext<SapEtaConfigurationStateModel>): void {
    ctx.setState(initialState);
  }
}
